import { useTRPC } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface UseShowcaseOptions {
  maxViews?: number; // number of times to show before suppressing or user dismisses
}

/**
 * Enables N time showcases (can be used for modals, tooltips, tours, etc)
 */
export function useShowcase(key: string, { maxViews = Infinity }: UseShowcaseOptions = {}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const profileQuery = useQuery(trpc.user.me.queryOptions());
  const recordViewMutation = useMutation(trpc.showcase.recordView.mutationOptions());
  const markCompletedMutation = useMutation({
    ...trpc.showcase.markCompleted.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.user.me.queryKey() }),
  });

  const status = profileQuery.data?.showcaseViews.find((s) => s.key === key);
  const shouldShow = profileQuery.isSuccess && !status?.completedAt && (status?.viewCount ?? 0) < maxViews;

  const recordView = useCallback(() => recordViewMutation.mutate({ key }), [key, recordViewMutation]);

  const complete = useCallback(() => markCompletedMutation.mutate({ key }), [key, markCompletedMutation]);

  return { shouldShow, recordView, complete };
}
